import {compactEth, formatPpm, formatTokens, type LeaderboardRow} from '@colosseum/shared';
import {api} from '../api.js';
import {tokenArt} from '../ui/components.js';
import {$, html, raw, setHtml} from '../ui/dom.js';

/** Ranked by fees paid, because fees paid is what a token's chance is made of. */

let window_: 'period' | 'all' = 'period';

export function leaderboardView(): string {
  return html`<section class="subpage">
    <div class="section-head">
      <div>
        <div class="eyebrow">Who is paying in</div>
        <h1>Leaderboard</h1>
      </div>
      <div class="filters">
        <button class="filter ${window_ === 'period' ? 'active' : ''}" data-action="lb" data-window="period">This period</button>
        <button class="filter ${window_ === 'all' ? 'active' : ''}" data-action="lb" data-window="all">All time</button>
      </div>
    </div>
    <p>A token's chance in a draw is its share of the fees paid in that period. This is that share, made explicit.</p>
    <div class="panel table-wrap"><div id="lb-body"><p class="muted">Loading…</p></div></div>
  </section>`;
}

export async function mountLeaderboard(): Promise<() => void> {
  // Rows navigate through a delegated handler rather than an inline `onclick`,
  // so the page carries no inline script and data never reaches a script
  // attribute, where HTML escaping would not protect it.
  const body = $('#lb-body');
  const go = (from: EventTarget | null) => {
    const row = (from as Element | null)?.closest<HTMLElement>('tr[data-href]');
    const href = row?.dataset.href ?? '';
    if (/^#\/token\/0x[0-9a-f]{40}$/i.test(href)) location.hash = href;
  };
  const onClick = (e: Event) => go(e.target);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      go(e.target);
    }
  };
  body?.addEventListener('click', onClick);
  body?.addEventListener('keydown', onKey);
  await paint();
  return () => {
    body?.removeEventListener('click', onClick);
    body?.removeEventListener('keydown', onKey);
  };
}

export async function setWindow(next: 'period' | 'all'): Promise<void> {
  window_ = next;
  for (const b of document.querySelectorAll<HTMLElement>('[data-action="lb"]')) {
    b.classList.toggle('active', b.dataset.window === next);
  }
  await paint();
}

async function paint(): Promise<void> {
  let rows: LeaderboardRow[] = [];
  try {
    rows = await api.leaderboard(window_);
  } catch {
    setHtml($('#lb-body'), html`<p class="muted">Could not reach the indexer.</p>`);
    return;
  }
  if (!rows.length) {
    setHtml($('#lb-body'), html`<p class="muted">Nothing has traded ${window_ === 'period' ? 'this period' : 'yet'}.</p>`);
    return;
  }
  const isPeriod = window_ === 'period';
  const allFees = rows.reduce((n, r) => n + BigInt(r.feesWei), 0n);
  setHtml(
    $('#lb-body'),
    html`<table class="leaderboard">
      <thead><tr>
        <th>#</th><th>TOKEN</th><th>FEES PAID</th><th>${isPeriod ? 'CHANCE' : 'SHARE'}</th>
        <th>VOLUME</th><th>BURNED</th><th>WINS</th>
      </tr></thead>
      <tbody>${rows.map((r, i) => {
        // In a period, the number that matters is the chance the contract would
        // give this token, which is zero below the threshold however much it
        // paid. All time, it is simply its share of all fees ever paid.
        const pct = isPeriod
          ? r.oddsPpm / 10_000
          : allFees === 0n
            ? 0
            : Number((BigInt(r.feesWei) * 1_000_000n) / allFees) / 10_000;
        const cell = isPeriod && !r.qualified
          ? html`<span class="muted small">below threshold</span>`
          : html`<div class="share-cell"><i style="width:${Math.max(1, pct).toFixed(1)}%"></i>
              <span>${formatPpm(Math.round(pct * 10_000))}</span></div>`;
        return raw(html`<tr tabindex="0" role="link" data-href="#/token/${r.token}">
          <td class="muted">${String(i + 1).padStart(2, '0')}</td>
          <td><div class="table-name">${raw(tokenArt(r))}<div>${r.name}<small>$${r.symbol}</small></div></div></td>
          <td>${compactEth(BigInt(r.feesWei))}</td>
          <td>${raw(cell)}</td>
          <td>${compactEth(BigInt(r.volumeWei))}</td>
          <td>${formatTokens(BigInt(r.burnedWei))}</td>
          <td>${r.wins || '—'}</td>
        </tr>`);
      })}</tbody>
    </table>
    <p class="muted small table-note">${window_ === 'period'
      ? 'Chance is what the contract would give this token if the draw ran now. A token below the 0.05 ETH threshold has no chance this period, whatever it paid.'
      : 'All-time fees across every period, including fees collected from graduated pools.'}</p>`,
  );
}
